import { Form } from 'antd';
import { FormButton, FormField, FormTextArea, FormTitle } from '../../../components/form/FormBasic';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import DefaultButton from '../../../components/buttons/DefaultButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import CanvasComponent from '../../../components/form/CanvasComponent';
import { useState } from 'react';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER } from '../../../constants/regex';

interface SignatureDetailsProps {
  form: FormInstance;
  handleSubmit?: () => void;
  handleCancel?: () => void;
  disabled?: boolean;
  isViewMode?: boolean;
  handleBack?: () => void;
}

const SignatureDetails: React.FC<SignatureDetailsProps> = ({
  form,
  handleSubmit,
  handleCancel,
  disabled,
  isViewMode = false,
  handleBack,
}) => {
  const { t } = useTranslation('inspection');

  const observations = useWatch('observations', form);
  const nameOfMetrologyOfficer = useWatch('nameOfMetrologyOfficer', form);
  const [hasDrawnMetrologySignature, setHasDrawnMetrologySignature] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const updateRepresentativeSignatureStatus = (drew: boolean, dataUrl: string) => {
    form.setFieldValue('hasRepresentativeSignature', drew);
    form.setFieldValue('signatureOfCompanyRep', dataUrl);
  };

  const updateOfficerSignatureStatus = (drew: boolean, dataUrl: string) => {
    form.setFieldValue('hasOfficerSignature', drew);
    form.setFieldValue('signatureOfMetrologyOfficer', dataUrl);

    setHasDrawnMetrologySignature(drew);
  };

  const toggleModal = () => {
    isModalOpen ? setIsModalOpen(false) : setIsModalOpen(true);
  };

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  const hasAllFieldsValue =
    !!observations && !!nameOfMetrologyOfficer && hasDrawnMetrologySignature;
  return (
    <div className="m-0 w-full p-4">
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('authorizationHeading')}
        </h2>
      </div>
      {/*  {!isViewMode && (
        <FormTitle
          label={t('completeRegistrationHeading')}
          styles="text-xl font-semibold text-black mb-4"
        />
      )} */}
      <div>
        <Form
          form={form}
          name="signatureFormData"
          layout="vertical"
          disabled={disabled || isViewMode}
          className={isViewMode ? 'view-mode-form' : ''}
        >
          <FormTextArea
            name="observations"
            label={t('observations')}
            rules={[
              { required: true, message: t('observationsRequired') },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('generalObservationsSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allow letters, numbers, space, punctuation and special characters
                  // const regex = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü\s,.\-!?;:()"'&@#$%^*+=/]+$/;                 
                  const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('observationsMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 200) {
                    return Promise.reject(t('observationsMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            placeholder={t('observationsPlaceholder')}
            disabled={false || isViewMode}
            isRequired={true}
            className="mb-4 md:mb-0"
          />

          <FormField
            name="nameOfMetrologyOfficer"
            label={t('nameOfMetrologyOfficer')}
            rules={[
              { required: true, message: t('nameOfMetrologyOfficerFieldRequired') },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('nameOfMetrologyOfficerSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allows letters, space and fullstop
                  // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]+$/;                  
                  const regex = REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('nameOfMetrologyOfficerMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('nameOfMetrologyOfficerMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            placeholder={t('nameOfMetrologyOfficerFieldPlaceholder')}
            disabled={false || isViewMode}
            type={FormFieldTypeEnum.INPUT}
            isRequired={true}
            allowLeadingTrailingSpaces={true}
            className="mobile-label-item mt-2 md:mt-0"
          />

          {!isViewMode ? (
            <Form.Item
              name="signatureOfMetrologyOfficer"
              label={t('metrologyOfficerSignatureLabel')}
              rules={[{ required: false, message: t('respondentSignatureFieldRequired') }]}
              required={true}
              className="mb-4 md:mb-2"
            >
              <CanvasComponent updateSignatureStatus={updateOfficerSignatureStatus} />
            </Form.Item>
          ) : (
            <Form.Item
              label={t('metrologyOfficerSignatureLabel')}
              className="mobile-label-item mt-4 md:mt-0"
            >
              {form.getFieldValue('signatureOfMetrologyOfficer') ? (
                <img
                  src={form.getFieldValue('signatureOfMetrologyOfficer')}
                  alt="Metrology Officer Signature"
                  className="mt-2 max-h-64 max-w-full rounded-lg border md:mt-0"
                />
              ) : (
                <p>{t('noSignature')}</p>
              )}
            </Form.Item>
          )}

          <FormField
            name="nameOfCompanyRep"
            label={t('nameOfCompanyRepresentative')}
            rules={[
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('nameOfCompanyRepresentativeSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allows letters, space and fullstop
                  // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]+$/;                 
                  const regex = REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER;                  
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('nameOfCompanyRepresentativeMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('nameOfCompanyRepresentativeMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            placeholder={
              !isViewMode ? t('nameOfCompanyRepresentativeFieldPlaceholder') : t('notGiven')
            }
            disabled={false || isViewMode}
            type={FormFieldTypeEnum.INPUT}
            isRequired={false}
            allowLeadingTrailingSpaces={true}
            className="mobile-label-item mt-2 md:mb-2 md:mt-0"
          />
          {!isViewMode ? (
            <Form.Item
              name="signatureOfCompanyRep"
              label={t('companyRepresentativeSignatureLabel')}
              rules={[
                { required: false, message: t('companyRepresentativeSignatureFieldRequired') },
              ]}
              className="mobile-label-item mt-4 md:mt-0"
            >
              <CanvasComponent updateSignatureStatus={updateRepresentativeSignatureStatus} />
            </Form.Item>
          ) : (
            <Form.Item
              label={t('companyRepresentativeSignatureLabel')}
              className="mobile-label-item mt-4 md:mt-0"
            >
              {form.getFieldValue('signatureOfCompanyRep') ? (
                <img
                  src={form.getFieldValue('signatureOfCompanyRep')}
                  alt="Metrology Officer Signature"
                  className="mt-2 max-h-64 max-w-full rounded-lg border md:mt-0"
                />
              ) : (
                <p className="pl-2">{t('notGiven')}</p>
              )}
            </Form.Item>
          )}

          {!isViewMode && (
            <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row">
              <DefaultButton handleOnClick={() => toggleCancelModal()} width="100%">
                {t('cancel')}
              </DefaultButton>
              <DefaultButton handleOnClick={() => handleBack?.()} width="100%">
                {t('back')}
              </DefaultButton>
              {hasAllFieldsValue ? (
                <PrimaryButton
                  handleOnClick={(e) => {
                    e.preventDefault();
                    toggleModal();
                  }}
                  width="100%"
                  disabled={disabled || isViewMode}
                  hideBgGradient={true}
                >
                  {t('submit')}
                </PrimaryButton>
              ) : (
                <SecondaryButton
                  handleOnClick={(e) => {
                    e.preventDefault();
                    handleSubmit?.();
                  }}
                  width="100%"
                  disabled={!hasAllFieldsValue || isViewMode}
                >
                  {t('submit')}
                </SecondaryButton>
              )}
            </div>
          )}

          <>
            <ConfirmModelComponent
              isOpen={isModalOpen}
              onClickedCancel={toggleModal}
              header={t('verificationSubmitModalTitle')}
              content={<p>{t('verificationSubmitModalConfirmaiton')}</p>}
              footerAction={{
                confimation: {
                  label: t('yes'),
                  onClick: handleSubmit ?? (() => {}),
                },
                cancel: {
                  label: t('no'),
                  onClick: toggleModal,
                },
              }}
              styleClasses="z-100"
            />

            <ConfirmModelComponent
              isOpen={isCancelModalOpen}
              onClickedCancel={toggleCancelModal}
              header={t('verificationCancelModalTitle')}
              content={<p>{t('verificationCancelModalConfirmaiton')}</p>}
              footerAction={{
                confimation: {
                  label: t('yes'),
                  onClick: handleCancel ?? (() => {}),
                },
                cancel: {
                  label: t('no'),
                  onClick: toggleCancelModal,
                },
              }}
              styleClasses="z-100"
            />
          </>
        </Form>
      </div>
    </div>
  );
};

export default SignatureDetails;
